import React, { useState, useRef, useLayoutEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setGameMode } from '../features/contestants/contestantsSlice';
import BackgroundContainer from '../components/BackgroundContainer';
import BaseButton from '../components/BaseButton';
import BaseHeader from '../components/BaseHeader';
import MenuButton from '../components/MenuButton';

const CARD_PADDING_Y = 12;

const modes = [
  {
    id: 'replay',
    headline: 'Replay Mode',
    description: 'Skip a card, replay a past round — dares are doubled.',
  },
  {
    id: 'drink',
    headline: 'Drink Mode',
    description: 'Skip a card, take a shot!',
  },
];

const ChooseGameMode = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const savedMode = useSelector((state) => state.contestants.gameMode);
  const [selectedMode, setSelectedMode] = useState(savedMode || 'drink');
  const contentRefs = useRef({});
  const [contentHeight, setContentHeight] = useState(null);

  useLayoutEffect(() => {
    const measure = () => {
      const contents = Object.values(contentRefs.current).filter(Boolean);
      if (contents.length === 0) return;
      const tallest = Math.max(...contents.map((el) => el.offsetHeight));
      setContentHeight(tallest);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const handleNext = () => {
    dispatch(setGameMode(selectedMode));
    navigate('/chooselevel');
  };

  return (
    <BackgroundContainer>
      <MenuButton showLogin={true} faq={true} showAccount={true} showDownload={true} />
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <BaseHeader
          sx={{
            marginBottom: '1rem',
            textAlign: 'center',
            fontSize: '4.2rem',
            '@media (max-width: 450px)': { fontSize: '3.9rem' },
            '@media (max-width: 410px)': { fontSize: '3.5rem' },
            '@media (max-width: 376px)': { fontSize: '3rem' },
            '@media (max-width: 320px)': { fontSize: '2.9rem' },
          }}
        >
          Game Mode
        </BaseHeader>

        {modes.map((mode) => {
          const isSelected = selectedMode === mode.id;
          return (
            <Box
              key={mode.id}
              onClick={() => setSelectedMode(mode.id)}
              sx={{
                width: '75%',
                maxWidth: '340px',
                minHeight: contentHeight ? `${contentHeight + CARD_PADDING_Y * 2}px` : undefined,
                boxSizing: 'border-box',
                margin: '8px auto',
                padding: `${CARD_PADDING_Y}px 24px`,
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                userSelect: 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                background: isSelected
                  ? 'linear-gradient(180deg, rgba(120, 30, 80, 0.6) 0%, rgba(60, 10, 40, 0.8) 100%)'
                  : 'linear-gradient(180deg, rgba(30, 10, 25, 0.7) 0%, rgba(20, 6, 18, 0.85) 100%)',
                border: isSelected
                  ? '1.5px solid rgba(205, 80, 150, 0.75)'
                  : '1.5px solid rgba(140, 50, 100, 0.3)',
                boxShadow: isSelected
                  ? '0 0 20px rgba(180, 60, 130, 0.35), 0 4px 16px rgba(0,0,0,0.5)'
                  : '0 2px 10px rgba(0,0,0,0.4)',
                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                '&:active': {
                  transform: 'scale(0.98)',
                  boxShadow: '0 0 14px rgba(180, 60, 130, 0.3), 0 2px 8px rgba(0,0,0,0.4)',
                },
              }}
            >
              <Box ref={(el) => { contentRefs.current[mode.id] = el; }}>
                <Typography
                  sx={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: '1.4rem',
                    fontWeight: 700,
                    color: isSelected ? '#e8c0d8' : '#b08898',
                    letterSpacing: '0.04em',
                    marginBottom: '6px',
                  }}
                >
                  {mode.headline}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Cormorant Garamond, serif',
                    fontSize: '1rem',
                    color: isSelected ? '#c49ab8' : '#8a6878',
                    letterSpacing: '0.02em',
                  }}
                >
                  {mode.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ marginTop: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
        <BaseButton onClick={() => navigate('/chooserelationship')}>Back</BaseButton>
        <BaseButton onClick={handleNext} disabled={!selectedMode}>Next</BaseButton>
      </Box>
    </BackgroundContainer>
  );
};

export default ChooseGameMode;
